import {create} from 'zustand';
import {ServiceProvider, ServiceProviderWithBrands} from '../models/serviceProvider.model';
import {serviceProviderFactory} from './serviceProviderFactory';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {useNotificationStore} from '../../notifications/store/notificationStore';
import {Pagination} from '../../../Data/types/BaseResponse';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {ProviderStats, BrandAssignment, BrandUnassignment, ProviderBrand, MyBrands, BrandStores, ApiError} from '../../../types/ServiceProviderTypes';

interface ServiceProviderState {
  // Data
  providers: ServiceProvider[];
  selectedProvider: ServiceProviderWithBrands | null;
  pagination: Pagination | undefined;

  // Loading states
  loading: boolean;

  // Error states
  error: string | null;

  // Actions
  fetchProviders: (page?: number, limit?: number) => Promise<void>;
  fetchProviderById: (id: string) => Promise<ServiceProviderWithBrands>;
  getProviderStats: (id: string) => Promise<ProviderStats>;
  createProvider: (provider: Omit<ServiceProvider, 'id' | 'status' | 'createdAt' | 'updatedAt'> & { password: string }) => Promise<ServiceProvider>;
  updateProvider: (id: string, provider: Partial<ServiceProvider>) => Promise<ServiceProvider>;
  deleteProvider: (id: string) => Promise<void>;
  assignBrand: (providerId: number, brandId: number) => Promise<BrandAssignment>;
  unassignBrand: (providerId: number, brandId: number) => Promise<BrandUnassignment>;
  getProviderBrands: () => Promise<ProviderBrand[]>;
  getMyBrands: () => Promise<MyBrands | ProviderBrand[] | undefined>;
  getBrandStores: (brandId: string) => Promise<BrandStores>;
  setSelectedProvider: (provider: ServiceProviderWithBrands | null) => void;
  setPagination: (pagination: Pagination | undefined) => void;
  refreshData: () => Promise<void>;
  clearError: () => void;
}

export const useServiceProviderStore = create<ServiceProviderState>((set, get) => {
  const errorHandlingService = StandardizedErrorHandler.getInstance();

  // Helper function to show notifications from Zustand store
  const showSuccess = (message: string) => {
    useNotificationStore.getState().addNotification({
      type: 'success',
      message,
      duration: 3000
    });
  };

  return {
    // Initial state
    providers: [],
    selectedProvider: null,
    pagination: undefined,
    loading: false,
    error: null,

    // Actions
    fetchProviders: async (page = 1, limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE) => {
      set({loading: true, error: null});
      try {
        const getAllUseCase = serviceProviderFactory.createGetAllServiceProvidersUseCase();
        const {items, pagination} = await getAllUseCase.execute(page, limit);
        set({providers: items, pagination, loading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message, loading: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    fetchProviderById: async (id: string) => {
      try {
        const getByIdUseCase = serviceProviderFactory.createGetServiceProviderByIdUseCase();
        const provider = await getByIdUseCase.execute(id);
        set({selectedProvider: provider});
        return provider;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    getProviderStats: async (id: string) => {
      try {
        const getStatsUseCase = serviceProviderFactory.createGetServiceProviderStatsUseCase();
        return await getStatsUseCase.execute(id);
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    createProvider: async (provider: Omit<ServiceProvider, 'id' | 'status' | 'createdAt' | 'updatedAt'> & { password: string }) => {
      try {
        const createUseCase = serviceProviderFactory.createCreateServiceProviderUseCase();
        const newProvider = await createUseCase.execute(provider);
        // Refresh data after creation
        await get().refreshData();
        // Show success notification
        showSuccess('تم إضافة مزود الخدمة بنجاح');
        return newProvider;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    updateProvider: async (id: string, provider: Partial<ServiceProvider>) => {
      try {
        const updateUseCase = serviceProviderFactory.createUpdateServiceProviderUseCase();
        const updatedProvider = await updateUseCase.execute(id, provider);
        // Refresh data after update
        await get().refreshData();
        // Show success notification
        showSuccess('تم تحديث مزود الخدمة بنجاح');
        return updatedProvider;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    deleteProvider: async (id: string) => {
      try {
        const deleteUseCase = serviceProviderFactory.createDeleteServiceProviderUseCase();
        await deleteUseCase.execute(id);
        // Refresh data after deletion
        await get().refreshData();
        // Success notification is handled by the hook/component level
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    assignBrand: async (providerId: number, brandId: number) => {
      try {
        const assignUseCase = serviceProviderFactory.createAssignBrandToProviderUseCase();
        await assignUseCase.execute(providerId, brandId);
        // Refresh selected provider data
        const selectedProvider = get().selectedProvider;
        if (selectedProvider) {
          await get().fetchProviderById(selectedProvider.id);
        }
        // Show success notification
        showSuccess('تم ربط البراند بنجاح');
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    unassignBrand: async (providerId: number, brandId: number) => {
      try {
        const unassignUseCase = serviceProviderFactory.createUnassignBrandFromProviderUseCase();
        const result = await unassignUseCase.execute(providerId, brandId);
        // Refresh selected provider data
        const selectedProvider = get().selectedProvider;
        if (selectedProvider) {
          await get().fetchProviderById(selectedProvider.id);
        }
        // Show success notification
        showSuccess('تم إلغاء ربط البراند بنجاح');
        return result;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    getProviderBrands: async () => {
      try {
        const getProviderBrandsUseCase = serviceProviderFactory.createGetProviderBrandsUseCase();
        return await getProviderBrandsUseCase.execute();
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    getMyBrands: async () => {
      try {
        const getMyBrandsUseCase = serviceProviderFactory.createGetMyBrandsUseCase();
        return await getMyBrandsUseCase.execute();
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    getBrandStores: async (brandId: string) => {
      try {
        const getBrandStoresUseCase = serviceProviderFactory.createGetBrandStoresUseCase();
        return await getBrandStoresUseCase.execute(brandId);
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    setSelectedProvider: (provider: ServiceProviderWithBrands | null) => {
      set({selectedProvider: provider});
    },

    setPagination: (pagination: Pagination | undefined) => {
      set({pagination});
    },

    refreshData: async () => {
      const currentState = get();
      const page = currentState.pagination?.page || PAGINATION_CONFIG.DEFAULT_PAGE;
      const limit = currentState.pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
      await get().fetchProviders(page, limit);
    },

    clearError: () => {
      set({error: null});
    }
  };
});
